import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import type * as monaco from "monaco-editor";
import { DiffCodeEditor, type MonacoEditorTheme } from "@ncoder/code-editor";
import { Maximize2, X as XIcon } from "lucide-react";
import { getMonacoLanguage } from "./monacoLanguage";
import type { OpenFile } from "./types";
import type { Dispatch, SetStateAction } from "react";

export function AcceptedCodeDiffSection({
  activeFileId,
  activeFile,
  acceptedCode,
  originalContentForDiff,
  editorTheme,
  diffEditorRef,
  diffOverlayOpen,
  setDiffOverlayOpen,
  diffHunkTotal,
  diffHunkResolved,
  setDiffHunkTotal,
  setDiffHunkResolved,
  setContentByFileId,
  setOriginalByFileId,
  setDirtyByFileId,
  onAcceptedCodeChange,
  handleSave,
}: {
  activeFileId: string;
  activeFile: OpenFile | null;
  acceptedCode: string;
  originalContentForDiff: string;
  editorTheme: MonacoEditorTheme;
  diffEditorRef: MutableRefObject<monaco.editor.IStandaloneDiffEditor | null>;
  diffOverlayOpen: boolean;
  setDiffOverlayOpen: Dispatch<SetStateAction<boolean>>;
  diffHunkTotal: number;
  diffHunkResolved: number;
  setDiffHunkTotal: Dispatch<SetStateAction<number>>;
  setDiffHunkResolved: Dispatch<SetStateAction<number>>;
  setContentByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  setOriginalByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  setDirtyByFileId: Dispatch<SetStateAction<Record<string, boolean>>>;
  onAcceptedCodeChange: (code: string | null) => void;
  handleSave: () => void;
}) {
  /** `onDidUpdateDiff` from inline onMount — dispose before editor teardown to avoid Monaco `Canceled` noise. */
  const diffHunkStatsListenerRef = useRef<monaco.IDisposable | null>(null);

  useEffect(() => {
    setDiffHunkResolved(0);
  }, [activeFileId, acceptedCode]);

  useEffect(() => {
    return () => {
      try {
        diffHunkStatsListenerRef.current?.dispose();
      } catch {
        /* already disposed */
      }
      diffHunkStatsListenerRef.current = null;
    };
  }, []);

  /** Inline editor unmounts when overlay opens — drop hunk-count listener tied to that instance. */
  useEffect(() => {
    if (!diffOverlayOpen) return;
    try {
      diffHunkStatsListenerRef.current?.dispose();
    } catch {
      /* */
    }
    diffHunkStatsListenerRef.current = null;
  }, [diffOverlayOpen]);

  /** Drop ref only — DiffCodeEditor owns Monaco dispose (see disposeStandaloneDiffEditor). */
  const disposeDiff = useCallback(() => {
    try {
      diffHunkStatsListenerRef.current?.dispose();
    } catch {
      /* */
    }
    diffHunkStatsListenerRef.current = null;
    diffEditorRef.current = null;
  }, [diffEditorRef]);

  /** Apply entire proposed side to baseline (both models) — clears remaining hunks. */
  const applyAcceptAll = useCallback(() => {
    const ed = diffEditorRef.current;
    if (!ed) {
      const accepted = acceptedCode;
      setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
      setContentByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
      setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
      setDiffHunkTotal(0);
      setDiffHunkResolved(0);
      setDiffOverlayOpen(false);
      onAcceptedCodeChange(null);
      return;
    }
    const origEd = ed.getOriginalEditor();
    const modEd = ed.getModifiedEditor();
    const om = origEd.getModel();
    const mm = modEd.getModel();
    if (!om || !mm) return;
    const accepted = mm.getValue();
    origEd.updateOptions({ readOnly: false });
    modEd.updateOptions({ readOnly: false });
    om.setValue(accepted);
    mm.setValue(accepted);
    origEd.updateOptions({ readOnly: true });
    modEd.updateOptions({ readOnly: true });
    setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
    setContentByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
    setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
    setDiffHunkTotal(0);
    setDiffHunkResolved(0);
    disposeDiff();
    setDiffOverlayOpen(false);
    onAcceptedCodeChange(null);
  }, [
    activeFileId,
    setContentByFileId,
    setOriginalByFileId,
    setDirtyByFileId,
    setDiffHunkResolved,
    setDiffHunkTotal,
    setDiffOverlayOpen,
    onAcceptedCodeChange,
    acceptedCode,
    disposeDiff,
    diffEditorRef,
  ]);

  /** Discard all proposed edits — modified becomes current original (includes prior per-hunk accepts). */
  const applyRejectAll = useCallback(() => {
    const ed = diffEditorRef.current;
    if (!ed) {
      const baseline = originalContentForDiff;
      setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
      setContentByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
      setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
      setDiffHunkTotal(0);
      setDiffHunkResolved(0);
      setDiffOverlayOpen(false);
      onAcceptedCodeChange(null);
      return;
    }
    const origEd = ed.getOriginalEditor();
    const modEd = ed.getModifiedEditor();
    const om = origEd.getModel();
    const mm = modEd.getModel();
    if (!om || !mm) return;
    const baseline = om.getValue();
    modEd.updateOptions({ readOnly: false });
    mm.setValue(baseline);
    modEd.updateOptions({ readOnly: true });
    setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
    setContentByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
    setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
    setDiffHunkTotal(0);
    setDiffHunkResolved(0);
    disposeDiff();
    setDiffOverlayOpen(false);
    onAcceptedCodeChange(null);
  }, [
    activeFileId,
    setContentByFileId,
    setOriginalByFileId,
    setDirtyByFileId,
    setDiffHunkResolved,
    setDiffHunkTotal,
    setDiffOverlayOpen,
    onAcceptedCodeChange,
    originalContentForDiff,
    disposeDiff,
    diffEditorRef,
  ]);

  const lang = activeFile
    ? getMonacoLanguage(activeFile.name, activeFile.metadataType)
    : "plaintext";

  /** Default Monaco diff uses ignoreTrimWhitespace — lines that differ only in leading/trailing spaces look identical, so getLineChanges() is empty and per-hunk UI never appears. */
  const devAgentDiffEditorOptions = { ignoreTrimWhitespace: false };
  const hasTextDiff = originalContentForDiff !== acceptedCode;
  const footerBulkActionsDisabled = !hasTextDiff && diffHunkTotal === 0;

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      {!diffOverlayOpen && (
        <>
          <div className="flex-1 min-h-0" style={{ height: "100%" }}>
            <DiffCodeEditor
              key={`diff-${activeFileId}`}
              original={originalContentForDiff}
              modified={acceptedCode}
              language={lang}
              theme={editorTheme}
              viewMode="inline"
              readOnly={true}
              editorOptions={devAgentDiffEditorOptions}
              inlineActions={true}
              onMount={(editor) => {
                diffEditorRef.current = editor;
                try {
                  diffHunkStatsListenerRef.current?.dispose();
                } catch {
                  /* */
                }
                diffHunkStatsListenerRef.current = null;
                const syncHunkTotal = () => {
                  try {
                    setDiffHunkTotal(editor.getLineChanges()?.length ?? 0);
                  } catch {
                    setDiffHunkTotal(0);
                  }
                };
                diffHunkStatsListenerRef.current = editor.onDidUpdateDiff(syncHunkTotal);
                syncHunkTotal();
                requestAnimationFrame(syncHunkTotal);
              }}
              onHunkAccept={(mergedBaseline) => {
                setOriginalByFileId((prev) => ({
                  ...prev,
                  [activeFileId]: mergedBaseline,
                }));
                setContentByFileId((prev) => ({
                  ...prev,
                  [activeFileId]: mergedBaseline,
                }));
                setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
                setDiffHunkResolved((prev) => prev + 1);
              }}
              onHunkReject={(_idx, updatedModified) => {
                setContentByFileId((prev) => ({
                  ...prev,
                  [activeFileId]: updatedModified,
                }));
                setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
                setDiffHunkResolved((prev) => prev + 1);
              }}
            />
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/30 flex-shrink-0 gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">
              {diffHunkTotal > 0
                ? `${diffHunkResolved}/${diffHunkTotal} changes reviewed`
                : "Review each change inline"}
            </span>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                type="button"
                disabled={footerBulkActionsDisabled}
                onClick={applyRejectAll}
                className="text-xs px-2.5 py-1 rounded border border-destructive/50 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                title="Reject every remaining change (modified → original)"
              >
                Reject all
              </button>
              <button
                type="button"
                disabled={footerBulkActionsDisabled}
                onClick={applyAcceptAll}
                className="text-xs px-2.5 py-1 rounded bg-green-600/90 text-white hover:bg-green-600 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                title="Accept every remaining change (baseline → proposed)"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={() => setDiffOverlayOpen(true)}
                className="p-1 rounded hover:bg-muted/50 text-muted-foreground transition-colors"
                title="Full-screen comparison"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => {
                  disposeDiff();
                  onAcceptedCodeChange(null);
                  setDiffOverlayOpen(false);
                }}
                className="text-xs px-2.5 py-1 rounded border border-border text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                Cancel
              </button>
              {(() => {
                const allReviewed =
                  diffHunkTotal === 0 || diffHunkResolved >= diffHunkTotal;
                return (
                  <button
                    type="button"
                    disabled={!allReviewed}
                    onClick={() => {
                      const modifiedValue =
                        diffEditorRef.current?.getModifiedEditor().getValue() ??
                        acceptedCode;
                      disposeDiff();
                      setContentByFileId((prev) => ({
                        ...prev,
                        [activeFileId]: modifiedValue,
                      }));
                      setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
                      onAcceptedCodeChange(null);
                      setDiffOverlayOpen(false);
                      setTimeout(() => {
                        try {
                          const models =
                            (window as unknown as { monaco?: { editor?: { getModels?: () => { uri?: { path?: string } }[]; setModelLanguage?: (m: unknown, lang: string) => void } } }).monaco?.editor?.getModels?.() || [];
                          const lang = activeFile
                            ? getMonacoLanguage(activeFile.name, activeFile.metadataType)
                            : "apex";
                          for (const m of models) {
                            if (
                              m.uri?.path?.includes(activeFileId) ||
                              m.uri?.path?.includes(activeFile?.name || "")
                            ) {
                              (
                                window as unknown as {
                                  monaco?: {
                                    editor?: {
                                      setModelLanguage?: (m: unknown, lang: string) => void;
                                    };
                                  };
                                }
                              ).monaco?.editor?.setModelLanguage?.(m, lang);
                            }
                          }
                        } catch {
                          /* non-critical */
                        }
                      }, 100);
                      setTimeout(() => handleSave(), 200);
                    }}
                    className={`text-xs px-2.5 py-1 rounded transition-colors ${
                      allReviewed
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-muted text-muted-foreground/60 cursor-not-allowed"
                    }`}
                    title={
                      allReviewed
                        ? "Apply changes and save"
                        : `Review all ${diffHunkTotal} changes first (${diffHunkResolved} done)`
                    }
                  >
                    Save
                  </button>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {diffOverlayOpen && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-6 py-3 border-b bg-muted/50 shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold">Comparing changes</span>
              {activeFile && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {activeFile.name}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                Original → Modified
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const baseline =
                    diffEditorRef.current
                      ?.getOriginalEditor()
                      .getModel()
                      ?.getValue() ?? originalContentForDiff;
                  setContentByFileId((prev) => ({
                    ...prev,
                    [activeFileId]: baseline,
                  }));
                  setOriginalByFileId((prev) => ({
                    ...prev,
                    [activeFileId]: baseline,
                  }));
                  setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
                  setDiffHunkTotal(0);
                  setDiffHunkResolved(0);
                  disposeDiff();
                  onAcceptedCodeChange(null);
                  setDiffOverlayOpen(false);
                }}
                className="text-xs px-3 py-1.5 rounded border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => {
                  const modifiedValue =
                    diffEditorRef.current?.getModifiedEditor().getValue() ??
                    acceptedCode;
                  disposeDiff();
                  setContentByFileId((prev) => ({
                    ...prev,
                    [activeFileId]: modifiedValue,
                  }));
                  setOriginalByFileId((prev) => ({
                    ...prev,
                    [activeFileId]: modifiedValue,
                  }));
                  setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
                  setDiffHunkTotal(0);
                  setDiffHunkResolved(0);
                  onAcceptedCodeChange(null);
                  setDiffOverlayOpen(false);
                }}
                className="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => {
                  diffEditorRef.current = null;
                  setDiffOverlayOpen(false);
                }}
                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Close overlay"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <DiffCodeEditor
              key={`diff-overlay-${activeFileId}`}
              original={originalContentForDiff}
              modified={acceptedCode}
              language={lang}
              theme={editorTheme}
              viewMode="side-by-side"
              readOnly={true}
              editorOptions={devAgentDiffEditorOptions}
              onMount={(editor) => {
                diffEditorRef.current = editor;
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
